import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from '../../hooks/useTranslation';

interface MultiSelectDropdownProps {
  label: string;
  options: string[];
  selectedOptions: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  label,
  options,
  selectedOptions,
  onChange,
  placeholder = 'Select...',
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = (option: string) => {
    const newSelected = selectedOptions.includes(option)
      ? selectedOptions.filter((s) => s !== option)
      : [...selectedOptions, option];
    onChange(newSelected);
  };

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([]);
  };

  const getButtonText = () => {
    if (selectedOptions.length === 0) {
      return placeholder;
    }
    if (selectedOptions.length === 1) {
      return selectedOptions[0];
    }
    return t('filters.selectedCount', { count: selectedOptions.length });
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-sm font-medium text-gray-300 mb-1">{label}</label>
      
      {/* Dropdown button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-200 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors"
      >
        <span className={selectedOptions.length === 0 ? 'text-gray-400' : ''}>
          {getButtonText()}
        </span>
        <svg
          className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown menu - opens UPWARD */}
      {isOpen && (
        <div className="absolute bottom-full left-0 right-0 mb-1 bg-gray-700 border border-gray-600 rounded-md shadow-xl z-50 max-h-64 overflow-y-auto">
          {/* Header with clear button */}
          {selectedOptions.length > 0 && (
            <div className="sticky top-0 bg-gray-700 border-b border-gray-600 px-3 py-2 flex items-center justify-between">
              <span className="text-sm text-gray-300">
                {t('filters.selectedCount', { count: selectedOptions.length })}
              </span>
              <button
                onClick={handleClearAll}
                className="text-xs text-indigo-400 hover:text-indigo-300 font-medium"
              >
                {t('filters.clearAll')}
              </button>
            </div>
          )}

          {/* Options list */}
          <div className="py-1">
            {options.map((option) => (
              <label
                key={option}
                className="flex items-center px-3 py-2 hover:bg-gray-600 cursor-pointer transition-colors"
              >
                <input
                  type="checkbox"
                  checked={selectedOptions.includes(option)}
                  onChange={() => handleToggle(option)}
                  className="rounded text-indigo-500 focus:ring-indigo-500 focus:ring-offset-gray-700"
                />
                <span className="ml-2 text-sm text-gray-200">{option}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;

